import {createFeatureSelector, createSelector} from '@ngrx/store';
import {currentCustomTrack, currentUgcPoi, currentUgcPoiDrawn} from '../features/ugc/ugc.selector';
import {UserActivityState} from './user-activity.reducer';
import {
  confFlowLineQuote,
  confOPTIONSShowTrackRemainingDistance,
  confPOIFORMS,
  confTRACKFORMS,
} from '../conf/conf.selector';
import {WmSlopeChartFlowLineQuote} from '@wm-types/slope-chart';

export const userActivity = createFeatureSelector<UserActivityState>('user-activity');

export const mapDetailsStatus = createSelector(
  userActivity,
  (state: UserActivityState) => state.mapDetailsStatus,
);
export const ugcOpened = createSelector(
  userActivity,
  (state: UserActivityState) => state.ugcOpened,
);
export const downloadsOpened = createSelector(
  userActivity,
  (state: UserActivityState) => state.downloadsOpened,
);
export const wmMapHitMapChangeFeatureId = createSelector(
  userActivity,
  (state: UserActivityState) => state.wmMapHitMapChangeFeatureById,
);
export const inputTyped = createSelector(
  userActivity,
  (state: UserActivityState) => state.inputTyped,
);
export const EmptyInputTyped = createSelector(
  userActivity,
  (state: UserActivityState) => state.inputTyped === '' || state.inputTyped === null,
);

export const UICurrentPoi = createSelector(userActivity, state =>
  state && state.currentPoi ? state.currentPoi : null,
);
export const UICurrentPoiId = createSelector(
  UICurrentPoi,
  UICurrentPoi => UICurrentPoi?.properties?.id ?? null,
);

export const drawTrackOpened = createSelector(userActivity, state =>
  state && state.drawTrackOpened ? state.drawTrackOpened : false,
);
export const drawPoiOpened = createSelector(userActivity, state =>
  state && state.drawPoiOpened ? state.drawPoiOpened : false,
);
export const drawOpened = createSelector(
  drawTrackOpened,
  drawPoiOpened,
  (drawTrackOpened, drawPoiOpened) => {
    return drawTrackOpened || drawPoiOpened;
  },
);

export const filterTracks = createSelector(userActivity, state => {
  return state.filterTracks;
});
export const trackFilterIdentifier = createSelector(filterTracks, filterTracks => {
  return filterTracks.map((f: any) => f.identifier);
});
export const lastFilterType = createSelector(userActivity, state => {
  return state.lastFilterType;
});
export const ecLayer = createSelector(userActivity, state => {
  return state.layer;
});

export const filterTaxonomies = createSelector(userActivity, state => {
  return state.filterTaxonomies;
});
export const mapFilters = createSelector(userActivity, state => {
  return {
    layer: state.layer,
    filterTracks: state.filterTracks,
    lastFilterType: 'tracks',
  };
});
export const poiFilterIdentifiers = createSelector(
  userActivity,
  state => state.poisSelectedFilterIdentifiers ?? [],
);

export const poisSelectedFilterIdentifiers = createSelector(
  userActivity,
  state => state.poisSelectedFilterIdentifiers,
);

export const loading = createSelector(userActivity, state => {
  return state.loading['pois'] || state.loading['layer'];
});
export const currentEcLayer = createSelector(userActivity, state => state.layer);

export const hasCustomPoi = createSelector(
  currentUgcPoiDrawn,
  currentUgcPoi,
  (ugcPoiDrawn, ugcPoi) => {
    return ugcPoiDrawn != null && ugcPoi == null;
  },
);

export const hasCustomTrack = createSelector(currentCustomTrack, currentCustomTrack => {
  return currentCustomTrack != null;
});

export const homeOpened = createSelector(
  hasCustomTrack,
  hasCustomPoi,
  (hasCustomTrack, hasCustomPoi) => {
    return !hasCustomTrack && !hasCustomPoi;
  },
);
export const chartHoverElements = createSelector(userActivity, state => state.chartHoverElements);
export const currentEcPoiId = createSelector(userActivity, state => state.currentEcPoiId);
export const onlyPoisFilter = createSelector(
  filterTracks,
  poisSelectedFilterIdentifiers,
  (ftracks, fpois) => {
    return (ftracks?.length ?? 0) === 0 && (fpois?.length ?? 0) > 0;
  },
);
export const showTracks = createSelector(
  currentEcLayer,
  onlyPoisFilter,
  ugcOpened,
  (currentLayer, onlyPoisFilter, ugcOpened) => {
    return currentLayer != null || !onlyPoisFilter || ugcOpened;
  },
);
export const showResult = createSelector(
  currentEcLayer,
  filterTracks,
  poisSelectedFilterIdentifiers,
  ugcOpened,
  downloadsOpened,
  inputTyped,
  (
    currentLayer,
    filterTracks,
    poisSelectedFilterIdentifiers,
    ugcOpened,
    downloadsOpened,
    inputTyped,
  ) => {
    const layerCondition = currentLayer != null;
    const filterTracksCondition = filterTracks.length > 0;
    const poisSelectedFilterIdentifiersCondition =
      poisSelectedFilterIdentifiers && poisSelectedFilterIdentifiers.length > 0;
    const inputTypedCondition = inputTyped != null && inputTyped != '';

    return (
      layerCondition ||
      filterTracksCondition ||
      poisSelectedFilterIdentifiersCondition ||
      inputTypedCondition ||
      ugcOpened ||
      downloadsOpened
    );
  },
);

export const flowLineQuoteText = createSelector(
  chartHoverElements,
  confFlowLineQuote,
  (elements, flowLine) => {
    if (!elements?.location?.altitude || !flowLine) return null;

    const {altitude} = elements.location;
    const {flow_line_quote_orange, flow_line_quote_red} = flowLine;
    const html =
      altitude < flow_line_quote_orange
        ? '<span class="green">Livello 1: tratti non interessati dall\'alta quota (quota minore di {{orange}} metri)</span>'
        : altitude > flow_line_quote_orange && altitude < flow_line_quote_red
        ? '<span class="orange">Livello 2: tratti parzialmente in alta quota (quota compresa tra {{orange}} metri e {{red}} metri)</span>'
        : '<span class="red">Livello 3: in alta quota (quota superiore {{red}} metri)</span>';

    const flowLineQuote: WmSlopeChartFlowLineQuote = {
      flow_line_quote_orange,
      flow_line_quote_red,
      html,
    };

    return flowLineQuote;
  },
);
export const wmMapHitmapFeatures = createSelector(userActivity, state => state.wmMapHitmapFeatures);
export const currentHitmapFeature = createSelector(
  wmMapHitMapChangeFeatureId,
  wmMapHitmapFeatures,
  (id, features) => {
    return features.find(f => f.properties.id == id);
  },
);

export const featuresInViewport = createSelector(userActivity, state => state.featuresInViewport);
export const nearbyLayerId = createSelector(userActivity, state => state.nearbyLayerId);
export const hasFeatureInViewport = createSelector(
  featuresInViewport,
  featuresInViewport => featuresInViewport && featuresInViewport.length > 0,
);
export const homeResultTabSelected = createSelector(
  userActivity,
  state => state.homeResultTabSelected,
);

export const currentDrawFormType = createSelector(
  confTRACKFORMS,
  confPOIFORMS,
  hasCustomTrack,
  (trackForms, poiForms, hasCustomTrack) => {
    return hasCustomTrack ? trackForms : poiForms;
  },
);

export const enableTrackRecorderPanel = createSelector(userActivity, state => state.enableTrackRecoderPanel);
export const enablePoiRecorderPanel = createSelector(userActivity, state => state.enablePoiRecorderPanel);
export const enableRecorderPanel = createSelector(
  enableTrackRecorderPanel,
  enablePoiRecorderPanel,
  (enableTrackRecorderPanel, enablePoiRecorderPanel) => {
    return enableTrackRecorderPanel || enablePoiRecorderPanel;
  },
);
export const onRecord = createSelector(userActivity, state => state.onRecord);
export const focusPosition = createSelector(userActivity, state => state.focusPosition);

export const enableTilesDownload = createSelector(userActivity, state => state.enableTilesDownload);
export const disableTilesDownloadButton = createSelector(
  userActivity,
  state => state.disableTilesDownloadButton,
);
export const wmMapTilesBoundingBox = createSelector(
  userActivity,
  state => state.wmMapTilesBoundingBox,
);

export const trackRemainingDistance = createSelector(
  userActivity,
  state => state.trackRemainingDistance,
);
export const trackDistanceCovered = createSelector(
  userActivity,
  state => state.trackDistanceCovered,
);
export const trackProgress = createSelector(userActivity, state => state.trackProgress);
export const trackPositionStale = createSelector(userActivity, state => state.trackPositionStale);

export interface TrackLiveDistanceVm {
  distanceCovered: number | null;
  remainingDistance: number | null;
  stale: boolean;
}

// Selettore condiviso tra tab-detail.component.ts (oc:8177, visualizzazione traccia) e
// track-recorder.component.ts (oc:8284, box di registrazione) — evita di duplicare in ogni
// consumer lo stesso gate su OPTIONS.showTrackRemainingDistance. Quando disabilitato, le
// distanze restano null a prescindere dal dato reale (il gate è su `enabled`, non sul valore
// numerico, perché distanceCovered/remainingDistance possono valere legittimamente 0).
export const trackLiveDistanceVm = createSelector(
  trackDistanceCovered,
  trackRemainingDistance,
  trackPositionStale,
  confOPTIONSShowTrackRemainingDistance,
  (distanceCovered, remainingDistance, stale, enabled): TrackLiveDistanceVm => ({
    distanceCovered: enabled !== false ? distanceCovered : null,
    remainingDistance: enabled !== false ? remainingDistance : null,
    stale,
  }),
);
